import { Box, Modal } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import {
  Dispatch,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import {
  Address,
  AddressComponent,
  AddressValidationResponse,
} from '../../../../../../../app/types/scheduling'
import { IOrder } from '../../../../../../../app/entities/Order'
import { MODES } from '../../../../../global/constants/scheduler'
import { fetchData } from '../../../../../global/utils/fetch'
import { ISchedulerStateProps } from '../container-modal/initialData'
import ContactForm from '../customer-information/contact/ContactForm'
import InstallationAddressForm from '../customer-information/installation-address/InstallationAddressForm'
import VehicleInformationForm from '../customer-information/vehicle-information/VehicleInformationForm'
import AddressConfirm from './address-confirm/AddressConfirm'
import { getAddressValidation } from './api'
import styles from './styles.module.css'
import {
  checkFormFields,
  handleWithCustomerVehicleId,
  handleWithoutCustomerVehicleId,
} from './utils'
import { isSchedulerPublicFacing } from '../../utils'

export interface ICustomerInformationData {
  //installation point
  installationPointObjectId: string
  address: string
  aptOrSuite: string
  additionalInformation: string
  city: string
  zipCode: string
  state: string

  //customer vehicle
  brandName: string
  model: string
  year: string
  variant: string
  brandId: string
  mileage: null | number

  //customer
  firstName: string
  lastName: string
  phone: string
  email: string
}

export interface IContactProps {
  formData: ICustomerInformationData
  setFormData: Dispatch<SetStateAction<ICustomerInformationData>>
  error?: boolean
  mode: (typeof MODES)[keyof typeof MODES]
}

export interface ICustomerInformationRefProps {
  setDisabled: Dispatch<SetStateAction<boolean>>
  setSchedulerState: Dispatch<SetStateAction<ISchedulerStateProps>>
  schedulerState: ISchedulerStateProps
  mode: (typeof MODES)[keyof typeof MODES]
  isCardOnFileCallback?: Function
  toggleToastError?: Function
  rescheduleReason?: string
  setRouteIndex?: Function
  viewOnly?: boolean
  order?: IOrder
}

function retrieveAddressValidation(
  formData: ICustomerInformationData,
): Promise<AddressValidationResponse> {
  const { address, city, state, zipCode } = formData

  return fetchData(
    getAddressValidation({
      state,
      city,
      zipCode,
      address,
    }),
  )
}

function getSuggestedComponentText(
  name: keyof Address,
  suggestedAddressComponents: AddressComponent[],
): string {
  return (
    suggestedAddressComponents.find((component) => component.property === name)
      ?.value || ''
  )
}

export const CustomerInformation = forwardRef(
  (props: ICustomerInformationRefProps, ref) => {
    const { toggleToastError, schedulerState, setDisabled, setSchedulerState } =
      props
    const mode = props.mode as (typeof MODES)[keyof typeof MODES]
    const {
      customerId,
      customerVehicleId,
      orderId,
      orderVehicleId,
      customerInformation,
    } = schedulerState

    const [isLoading, setIsLoading] = useState(false)
    const [formError, setFormError] = useState(false)
    const [formData, setFormData] =
      useState<ICustomerInformationData>(customerInformation)
    const [seeAddressConfirmModal, setSeeAddressConfirmModal] = useState(false)
    const [suggestedAddressComponents, setSuggestedAddressComponents] =
      useState<AddressComponent[]>([])
    const [isBlacklisted, setIsBlacklisted] = useState(false)
    const [isEditButtonDisabled, setIsEditDisabled] = useState(
      !isSchedulerPublicFacing(mode) ||
        checkFormFields(formData, isBlacklisted),
      // the `|| checkFormFields(formData)` handles when we're in a public
      // page but the form fields have already been edited, navigated to
      // another page, and then navigated back
    )
    const [shouldCheckCustomerFormFields, setShouldCheckCustomerFormFields] =
      useState(true)

    function isCustomerEditing() {
      return isSchedulerPublicFacing(mode) && isEditButtonDisabled
    }

    function calculateShouldCheckCustomerFormFields() {
      // If we're in a public facing page and the cst has not edited the
      // ContactForm fields, we don't want to check the form fields as
      // they will contain redacted info and won't be valid
      return !isSchedulerPublicFacing(mode) || isCustomerEditing()
    }
    useEffect(() => {
      setShouldCheckCustomerFormFields(calculateShouldCheckCustomerFormFields())
    }, [mode, isEditButtonDisabled])

    function overwriteAddressWithSuggestedComponents(
      suggestedAddressComponents: AddressComponent[],
    ) {
      const newCustomerFormData = {
        ...formData,
        state:
          getSuggestedComponentText('state', suggestedAddressComponents) ||
          formData.state,
        zipCode:
          getSuggestedComponentText('zipCode', suggestedAddressComponents) ||
          formData.zipCode,
        city: getSuggestedComponentText('city', suggestedAddressComponents),
        address: getSuggestedComponentText(
          'address',
          suggestedAddressComponents,
        ),
      }

      setFormData(newCustomerFormData)

      return newCustomerFormData
    }

    function onAddressConfirmChange() {
      setSeeAddressConfirmModal(false)
      overwriteAddressWithSuggestedComponents(suggestedAddressComponents)
    }

    async function handleNextLogic(successCallback: Function) {
      setIsLoading(true)
      setDisabled(true)

      const { verdict, addressComponents } = await retrieveAddressValidation(
        formData,
      )

      setSuggestedAddressComponents(addressComponents)

      const addressIsInvalid = !verdict.addressComplete

      if (addressIsInvalid) {
        setFormError(true)
        setIsLoading(false)
        return
      }

      const addressIsValidWithReplacedValues = verdict.hasReplacedComponents

      if (addressIsValidWithReplacedValues) {
        setIsLoading(false)
        setSeeAddressConfirmModal(true)
        return
      }

      const overwrittenFormData =
        overwriteAddressWithSuggestedComponents(addressComponents)

      if (mode === MODES.FINISH || mode === MODES.CUSTOMER) {
        await handleSaveClickLogic()
      }

      try {
        setSchedulerState({
          ...schedulerState,
          customerInformation: overwrittenFormData,
        })
        successCallback(true)
      } catch (error) {
        console.error('An error occurred:', error)
        toggleToastError && toggleToastError(true)
        successCallback(false)
        setFormError(true)
      } finally {
        setDisabled(false)
        setIsLoading(false)
      }
    }
    async function handleSaveClickLogic() {
      setIsLoading(true)
      setDisabled(true)
      setSchedulerState({
        ...schedulerState,
        customerInformation: formData,
      })

      const shouldSaveCustomer = calculateShouldCheckCustomerFormFields()
      if (!customerVehicleId) {
        await handleWithoutCustomerVehicleId(
          customerId,
          formData,
          orderId,
          setSchedulerState,
          shouldSaveCustomer,
        )
      } else {
        await handleWithCustomerVehicleId(
          customerId,
          customerVehicleId,
          formData,
          orderId,
          orderVehicleId,
          shouldSaveCustomer,
        )
      }
    }

    function handleEditClicked() {
      setFormData({
        ...formData,
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
      })

      setIsEditDisabled(true)
    }

    useImperativeHandle(
      ref,
      () => ({
        async handleNextClick(successCallback: Function) {
          await handleNextLogic(successCallback)
        },
        async handleSaveClick() {
          await handleSaveClickLogic()
        },
      }),
      [formData, mode],
    )

    useEffect(() => {
      if (formError) {
        setFormError(false)
      }
      const formFieldCheck = checkFormFields(
        formData,
        isBlacklisted,
        !shouldCheckCustomerFormFields,
      )
      if (formFieldCheck) {
        setDisabled(false)
      } else {
        setDisabled(true)
      }
    }, [formData])

    return (
      <>
        <div
          className={
            mode === MODES.CUSTOMER
              ? styles.responsiveFormContainer
              : styles.formContainer
          }
        >
          {isLoading && (
            <div className={styles.overlay}>
              <div className={styles.progressContainer}>
                <CircularProgress size={80} />
              </div>
            </div>
          )}
          <ContactForm
            formData={formData}
            setFormData={setFormData}
            mode={mode}
            isEditDisabled={isEditButtonDisabled}
            handleEdit={handleEditClicked}
          />
          <InstallationAddressForm
            mode={mode}
            formData={formData}
            setFormData={setFormData}
            error={formError}
          />
          <VehicleInformationForm
            formData={formData}
            setFormData={setFormData}
            mode={mode}
            setDisabled={setDisabled}
            isBlacklisted={isBlacklisted}
            setIsBlacklisted={setIsBlacklisted}
          />
          <div>
            <Modal open={seeAddressConfirmModal}>
              <Box className={styles.addressConfirmBox}>
                <AddressConfirm
                  inputtedAddress={formData}
                  suggestedAddressComponents={suggestedAddressComponents}
                  setSeeAddressConfirmModal={setSeeAddressConfirmModal}
                  onConfirm={onAddressConfirmChange}
                />
              </Box>
            </Modal>
          </div>
        </div>
      </>
    )
  },
)
